import React from 'react';
import { motion } from 'motion/react';

import {
  Globe,
  Code,
  Palette,
  Rocket,
  Shield,
  Zap,
  Search,
  ShoppingCart,
  CheckCircle,
  ArrowRight,
  Monitor,
  Server,
  Layout,
  TrendingUp,
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SpamProtectedForm from '../../components/SpamProtectedForm';
import '../../styles/services.css';

const features = [
  { icon: Layout, title: 'Responsive Design', desc: 'Websites that look perfect on all devices' },
  { icon: Code, title: 'Custom Development', desc: 'Tailored solutions built from scratch' },
  { icon: ShoppingCart, title: 'E-Commerce', desc: 'Powerful online stores with secure payments' },
  { icon: Search, title: 'SEO Optimization', desc: 'Built-in SEO best practices for visibility' },
  { icon: Server, title: 'CMS Solutions', desc: 'Easy-to-manage content management systems' },
  { icon: Zap, title: 'Performance', desc: 'Lightning-fast load times and optimization' },
];

const process = [
  { step: '01', title: 'Strategy', desc: 'Define goals, audience, and requirements' },
  { step: '02', title: 'Design', desc: 'Create stunning visual designs and prototypes' },
  { step: '03', title: 'Development', desc: 'Build with modern, scalable technologies' },
  { step: '04', title: 'Testing', desc: 'Cross-browser and device testing' },
  { step: '05', title: 'Launch', desc: 'Deploy with zero downtime' },
  { step: '06', title: 'Growth', desc: 'Analytics and continuous improvement' },
];

const WebDevelopmentPage = () => {
  return (
    <div className="service-page">
      <Header />
      
      {/* Hero */}
      <section className="service-hero web-dev">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="service-hero-content"
          >
            <span className="service-badge">
              <Globe size={16} /> Web Development
            </span>
            <h1>Build Your Digital Presence</h1>
            <p>We create stunning, high-performance websites that engage visitors and drive conversions for businesses of all sizes.</p>
            <div className="hero-buttons">
              <a href="#contact" className="btn-service-primary">
                Get a Quote <ArrowRight size={18} />
              </a>
              <a href="#features" className="btn-service-secondary">
                Our Services
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="service-features" id="features">
        <div className="container-custom">
          <div className="section-header-service">
            <span className="section-tag">What We Offer</span>
            <h2>Complete Web Development Solutions</h2>
            <p>From simple landing pages to complex web applications</p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="feature-card"
              >
                <div className="feature-icon blue">
                  <feature.icon size={24} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="service-process">
        <div className="container-custom">
          <div className="section-header-service">
            <span className="section-tag">Our Process</span>
            <h2>From Concept to Launch</h2>
            <p>A streamlined process for delivering exceptional websites</p>
          </div>
          
          <div className="process-grid">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="process-card"
              >
                <span className="process-step blue">{item.step}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="service-contact" id="contact">
        <div className="container-custom">
          <div className="contact-wrapper">
            <div className="contact-info">
              <h2>Start Your Web Project</h2>
              <p>Let's discuss how we can help you build an amazing online presence.</p>
              <ul className="contact-benefits">
                <li><CheckCircle size={20} /> Free consultation</li>
                <li><CheckCircle size={20} /> Custom design mockups</li>
                <li><CheckCircle size={20} /> Competitive pricing</li>
                <li><CheckCircle size={20} /> Ongoing support</li>
              </ul>
            </div>
            
            <SpamProtectedForm 
              emailSubject="Web Development Inquiry"
              submitText="Send Message"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WebDevelopmentPage;
